import { AnimatePresence, motion } from "framer-motion";
import { getLoadedSceneDefinition, useSceneStore } from "@/engine/SceneManager";
import { tokens } from "@/art/tokens";
import type { SceneTransition } from "@/scenes/SceneContract";

/**
 * 현재 씬 스택 최상단을 렌더하고, 전환 애니메이션을 관장.
 * Framer Motion 의 AnimatePresence 로 mode="wait" — 이전 씬 exit 후 새 씬 enter.
 */
export function SceneHost() {
    const stack = useSceneStore(s => s.stack);
    const registry = useSceneStore(s => s.registry);
    const push = useSceneStore(s => s.push);
    const replace = useSceneStore(s => s.replace);

    const top = stack[stack.length - 1];
    if (!top) return null;
    const def = getLoadedSceneDefinition(registry[top.id]);
    if (!def) return null;

    const transition = def.manifest.transitions;
    const variants = getVariants(transition.enter, transition.exit);

    const handleExit = (nextId?: string, params?: unknown) => {
        if (!nextId) return;
        void replace(nextId as never, params);
    };

    // push 를 쓰고 싶은 시나리오는 씬이 직접 useSceneStore 에서 뽑아 쓰면 됨.
    // exit 콜백은 편의용 replace 경로만 제공.
    void push;

    return (
        <AnimatePresence mode="wait" initial={false}>
            <motion.div
                key={top.id}
                initial={variants.initial}
                animate={variants.animate}
                exit={variants.exit}
                transition={{
                    duration: (transition.enterDuration ?? tokens.timing.sceneEnter) / 1000,
                    ease: tokens.easing.sceneCurtain,
                }}
                className="absolute inset-0"
            >
                {def.render({ params: top.params, exit: handleExit })}
            </motion.div>
        </AnimatePresence>
    );
}

function getVariants(enter: SceneTransition, exit: SceneTransition) {
    const pose = (t: SceneTransition) => {
        switch (t) {
            case "fade":        return { opacity: 0 };
            case "slide-left":  return { opacity: 0, x: -60 };
            case "slide-right": return { opacity: 0, x: 60 };
            case "iris":        return { opacity: 0, scale: 0.92 };
            case "curtain":     return { opacity: 0, scale: 1.02, y: 12 };
            default:            return { opacity: 0 };
        }
    };
    return {
        initial: pose(enter),
        animate: { opacity: 1, x: 0, y: 0, scale: 1 },
        exit: pose(exit),
    };
}
